import base64 from 'base-64';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import styled from 'styled-components';
import * as Yup from 'yup';
import { device } from '../../utils/device';
import Input from '../Input';
import Label from '../Label';
import Button from '../Button';
import FormikPhoneInput from './FormikPhoneInput';

const Page = styled.div`
  display: flex;
  justify-content: center;
  flex-grow: 2;

  @media ${device.laptopL} {
    flex-direction: column;
  }
`;

const MaxWidth = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.colors.white};
  width: 100%;
  padding: 80px;
  flex-grow: 1;

  @media ${device.laptopL} {
    padding: 20px;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 80px;
  background: ${props => props.theme.colors.background.blueGradient};
  height: 100vh;
  width: 100%;
  color: ${props => props.theme.colors.white};

  @media ${device.laptopL} {
    height: 100%;
    width: 100%;
    padding: 50px 100px;
  }

  h1 {
    font-size: 40px;
    font-weight: 800;
    width: 400px;
    margin-bottom: 100px;

    @media ${device.laptopL} {
      height: 100%;
      width: 100%;
      margin-bottom: 50px;
      text-align: center;
      font-size: 30px;
    }
  }

  ul {
    list-style: none;
    width: 400px;
    
    @media ${device.laptopL} {
      height: 100%;
      width: 100%;
      font-size: 14px;
    }
  }

  li {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
  }
`;

export const Form = styled.form`
  width: 100%;
  padding: 30px;
  box-shadow: ${props => props.theme.shadows.card};
`;

export const Feedback = styled.p`
  color: ${props => props.theme.colors.red};
  font-size: 12px;
  margin-top: 5px;
`;

Form.Feedback = Feedback;

export const FormGroup = styled.div`
  margin: 10px;
  width: 100%;

  & > input {
    margin-top: 5px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media ${device.laptopL} {
      flex-direction: column;
      width: 100%;
    }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
`;

const IconItem = styled.div`
  width: 15px;
  height: 15px;
  background: ${props => props.theme.colors.background.orangeGradient};
  margin-right: 15px;
  padding-right: 15px;
`;

const PhoneContainer = styled.div`
  input {
    height: 42px;
    width: 100%;
    font-size: 16px;
    background-color: ${props => props.theme.colors.veryLightBlue};
    border: ${props => props.error === true ? `.5px solid ${props.theme.colors.red}` : 
    `.5px solid ${props.theme.colors.lightGray}`};
    
    border-radius: 2px;
    padding: 15px;
    color: ${props => props.theme.colors.text.gray};

    &:focus {
      border: .5px solid ${props => props.theme.colors.mediumBlue};
    }

    &:invalid {
      border: .5px solid ${props => props.theme.colors.red};
    }
  }
`; 

const NotificationContainer = styled.div`
  border: 2px solid #001e42;
  border-radius: 2px;
  position: relative;
  padding: 10px 5px 10px 90px;
  margin: 20px 0;

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    text-align: center;
    content: '!';
    background: #001e42;
    height: 100%;
    min-height: 100%;
    width: 80px;
    line-height: 90px;
    font-size: 50px;
    color: #fff;
  }
`;

const ButtonsContainer = styled.div`
  & > * {
    margin-left: 20px;
  }

  @media ${device.laptopL} {
      display: flex;
      flex-direction: column;
       
       & > * {
         margin-bottom: 15px;
       }
    }
`;

function login(email, password, alias, redirect) {
  const form = document.createElement('form');
  form.action = `${process.env.GATSBY_API_URL}/login`;
  form.method = 'POST';
  form.style.display = 'none';

  const emailInput = document.createElement('input');
  emailInput.name = 'login';
  emailInput.value = email;
  emailInput.type = 'hidden';
  form.appendChild(emailInput);

  const passwordInput = document.createElement('input');
  passwordInput.name = 'password';
  passwordInput.value = password;
  passwordInput.type = 'hidden';
  form.appendChild(passwordInput);

  const aliasInput = document.createElement('input');
  aliasInput.name = 'alias';
  aliasInput.value = alias;
  aliasInput.type = 'hidden';
  form.appendChild(aliasInput);

  const redirectInput = document.createElement('input');
  redirectInput.name = 'redirect_url';
  redirectInput.value = redirect;
  redirectInput.type = 'hidden';
  form.appendChild(redirectInput);

  document.body.appendChild(form);
  form.submit();
}

function SignUpForm() {

  const [defaultCountry, setDefaultCountry] = useState('');
  const ref = React.createRef();
  const [haveRecruiterDomain, setHaveRecruiterDomain] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [longEnough, setLongEnough] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);

  useEffect(() => {
    fetch('https://ipinfo.io',  { headers: {
      'Accept': 'application/json'
    }})
   .then( res => res.json())
   .then(response => {
    setDefaultCountry(response.country);
  })}, []);

  const handleChange = (e) => {
    formik.setFieldValue("phone_number", e);
  }

  const isValidEmailDomain = (value) => {
    let valid = true;
    const invalidDomains = [
      'gmail.com',
      'yahoo.com',
      'outlook.com',
      'hotmail.com',
      'yandex.com',
      'aol.com',
      'zoho.com',
      'mail.com',
      'gmx.com',
      'protonmail.com',
      'icloud.com'	
    ];

    if(value) {
      invalidDomains.forEach(invalid => {
        const domain = value.split('@')[1].toLowerCase();
        if(invalid === domain) {
          valid = false;
        }
      });
    }

    return valid;
  }

  Yup.addMethod(Yup.string, 'isValidEmailDomain', function (errorMessage) {
    return this.test('test-valid-email-domain', errorMessage, function (value) {
      const { path, createError } = this;
      // handle empty field
      if( value === undefined) {
        return true;
      } else if (!value.includes('@')) {
        return createError({path, message: 'Your email must contain an @'})
      } else if (value.split('@')[1].length < 1) {
        return createError({path, message: 'Your email must have a domain'})
      }
      return (
          isValidEmailDomain(value) ||
          createError({ path, message: errorMessage })
      );
    })
  })

  Yup.addMethod(Yup.string, 'isValidPhoneNumber', function (errorMessage) {
    return this.test('test-valid-phone-number', errorMessage, function (value) {
      // handle empty field
      if( value === undefined ) {
        return true;
      }
      const { path, createError } = this;
      return (
          isValidPhoneNumber(value) ||
          createError({ path, message: errorMessage })
      );
    })
  })

  const formik = useFormik({
    initialValues: {
      organisation: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      password: '',
      password_confirmation: '',
    },
    validate: (values) => {
      values.password.length < 8 ? setLongEnough(false) : setLongEnough(true);
      !/\d/.test(values.password) ? setHasNumber(false) : setHasNumber(true);
      !/[A-Z]/.test(values.password) ?  setHasUpperCase(false) : setHasUpperCase(true)
    },
    validationSchema: Yup.object({
      organisation: Yup.string()
        .required('Please provide your Organisation Name')
        .max(50, "Organisation cannot be longer than 50 characters"),
      first_name: Yup.string()
        .required('Please provide your First Name')
        .max(50, "First name cannot be longer than 50 characters"),
      last_name: Yup.string()
        .required('Please provide your Last Name')
        .max(50, "Last name cannot be longer than 50 characters"),
      email: Yup.string().email('Please provide a valid E-mail Address')
        .required('Please provide your Organisation Email')
        .max(254, "E-mail cannot be longer than 254 characters")
        .isValidEmailDomain(
          'You must sign-up with your company email address. We do not allow Gmail, Yahoo and other free email accounts. This is because you will be creating assessments on behalf of your company, and we must authenticate you'
        ),
      phone_number: Yup.string()
        .required('Please provide your Organisation Phone')
        .isValidPhoneNumber( 'Invalid Phone Number'),
      password_confirmation: Yup.string()
      .required("Please confirm your Password")
      .when("password", {
        is: password => (password && password.length > 0 ? true : false),
        then: () => Yup.string().oneOf([Yup.ref("password")], "Password doesn't match")
      })
    }),
    onSubmit: async (values, { resetForm }) => {
      if(!longEnough || !hasNumber || !hasUpperCase) {
        return;
      }

      const toUrlEncoded = obj => Object.keys(obj).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(obj[k])).join('&');
      
      if(values.notify) {
        values.notify_admin = 1;
      }

      if(values.continue) {
        values.sign_up_anyway = 1;
      }

      const headers = new Headers();
      headers.append('Content-Type', "application/x-www-form-urlencoded");
      
      if(process.env.GATSBY_SERVER_AUTH) {
        headers.append('Authorization', 'Basic ' + base64.encode(process.env.GATSBY_SERVER_AUTH));
      }

      try {
        setLoading(true);

        // Clear possible existing session before registering
        await fetch(`${process.env.GATSBY_NEW_APP_URL}/clear-local-storage`, {
          method: 'GET',
          mode: "no-cors"
        });

        const response = await fetch(`${process.env.GATSBY_API_URL}/alooba-for-work-sign-up-ajax`, {
          method: 'POST',
          body: toUrlEncoded(values),
          headers: headers,
          mode: "cors"
        });

        const res = await response.json();
        if(!res.success) {
          if(res.errors.have_recruiter_email_domain) {
            setHaveRecruiterDomain(true);
          }

          setErrors(res.errors);
          setLoading(false);
          return;
        }

        login(values.email, values.password, res.alias, res.redirect_url);

      } catch(err) {
        setLoading(false);
      }
    },
  });

  return (
    <LoadingOverlay active={loading} spinner>
    <Page>
      <LeftContainer>
        <h1>Screen candidates and assess your team</h1>
        <ul>
          <li><IconItem />Efficiently and fairly assess your candidates across over 20 core subjects in data (including Analytics, Python, R, SQL, Statistics, Visualisations and more)</li>
          <li><IconItem />Save wasted time interviewing unsuitable candidates</li>
          <li><IconItem />Get a clear picture of a candidate’s strengths and weaknesses prior to interview</li>
          <li><IconItem />Screen candidates quickly and efficiently using our MCQs, or do a deepdive into a candidate’s abilities with our assignments</li>
        </ul>
      </LeftContainer>
      <MaxWidth>
          <Form onSubmit={formik.handleSubmit}>
          {haveRecruiterDomain && 
          <NotificationContainer>
            <p>It looks like someone from your organisation has already created an account with Alooba.
               You can open a separate company account if you really want to, but otherwise, 
               you can notify the owner of the current account and they will be able to grant you access to 
            the existing account.</p> 
          </NotificationContainer>}
            <Row>
              <FormGroup>
                <Label htmlFor="organisation" required>Organisation Name</Label>
                <Input id="organisation" 
                    type="text" 
                    value={formik.values.organisation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!!formik.errors.organisation && formik.touched.organisation ? "true" : "false"}
                />
                {formik.errors.organisation && formik.touched.organisation && (
                    <Form.Feedback>{formik.errors.organisation}</Form.Feedback>)}
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Label htmlFor="first_name" required>Your First Name</Label>
                <Input id="first_name" type="text" 
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!!formik.errors.first_name && formik.touched.first_name ? "true" : "false"}     
                />
                {formik.errors.first_name && formik.touched.first_name && (
                    <Form.Feedback>{formik.errors.first_name}</Form.Feedback>)}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="last_name" required>Your Last Name</Label>
                <Input id="last_name" type="text" 
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}    
                  error={!!formik.errors.last_name && formik.touched.last_name ? "true" : "false"}   
                />
                {formik.errors.last_name && formik.touched.last_name && (
                    <Form.Feedback>{formik.errors.last_name}</Form.Feedback>)}
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Label htmlFor="email" required>Your Organisation Email</Label>
                <Input id="email" type="email" 
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.email && formik.touched.email ? "true" : "false"}   
                />
                {formik.errors.email && formik.touched.email && (
                    <Form.Feedback>{formik.errors.email}</Form.Feedback>)}
                {errors && errors.email && (
                  <Form.Feedback>{errors.email}</Form.Feedback>)}
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Label htmlFor="phone_number" required>Your Direct Phone Number</Label>
                <PhoneContainer>
                <FormikPhoneInput
                  defaultCountry={defaultCountry}
                    ref={ref}
                    name="phone_number"
                    formik={formik}
                    onChange={handleChange}
                  />
                </PhoneContainer>
                {formik.errors.phone_number && formik.touched.phone_number && (
                    <Form.Feedback>{formik.errors.phone_number}</Form.Feedback>)}
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Label htmlFor="password" required>Password</Label>
                <Input id="password" type="password" 
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && (!longEnough || !hasNumber || !hasUpperCase ) ? "true" : "false"}     
                />
                {
                  <div style={{ padding: '8px 8px 0', fontSize: 'smaller', color: '#888' }}>
                    Password requirements:
                    <ul style={{ padding: '0', margin: '2px 0 0 24px' }}>
                      <li style={ formik.touched.password && !longEnough ? { color: '#D64045' } : { color: '#888'} }>be at least 8 characters long</li>
                      <li style={ formik.touched.password && !hasNumber ? { color: '#D64045' } : { color: '#888'} }>contains at least 1 number</li>
                      <li style={ formik.touched.password && !hasUpperCase ? { color: '#D64045' } : { color: '#888'} }>contains at least 1 upper case letter</li>
                    </ul>
                  </div>
                }
              </FormGroup>
            </Row>
            <Row>
              <FormGroup>
                <Label htmlFor="password_confirmation" required>Confirm Password</Label>
                <Input id="password_confirmation" type="password" 
                  value={formik.values.password_confirmation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.password_confirmation && formik.touched.password_confirmation ? "true" : "false"}     
                />
                {formik.errors.password_confirmation && formik.touched.password_confirmation && (
                  <Form.Feedback>{formik.errors.password_confirmation}</Form.Feedback>)}
              </FormGroup>
            </Row>
            <Center>
              {haveRecruiterDomain ?
              <ButtonsContainer>
                <Button.Blue type="button"
                  onClick={() => { 
                    formik.setFieldValue('continue', true);
                    formik.setFieldValue('notify', false);
                    formik.handleSubmit()}}
                  >Continue Anyway</Button.Blue>
                <Button.Blue type="button"
                  onClick={() => { 
                    formik.setFieldValue('continue', false);
                    formik.setFieldValue('notify', true);
                    formik.handleSubmit()}}>Notify Account Owner</Button.Blue>
              </ButtonsContainer>
              :
                  <Button.Blue type="submit">Submit</Button.Blue>
              }
            </Center>
          </Form>
      </MaxWidth>
    </Page>
    </LoadingOverlay>
  );
}

export default SignUpForm;
